import {
  add,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  startOfMonth,
  startOfWeek,
} from 'date-fns';

import { dateRangeToStartEnd } from '../../utils/dateRangeToStartEnd';
import {
  KiSyokenDateRangeParams,
  KiSyokenDateUnit,
  KiSyokenSearchParams,
} from './types';

/**
 * [機種別商圏] 主期間と比較期間の開始日・終了日
 */
export type KiSyokenDateRangePeriod = {
  startDate: string;
  endDate: string;
  startComparisonDate: string;
  endComparisonDate: string;
};

/**
 * [機種別商圏] 検索パラメータに渡す日付データを算出する
 */
export const makeKiSyokenDateFromDateRange = ({
  dateRangeParams,
  searchParams,
  baseDate = new Date(),
  excludeToday = true,
  customDate,
}: {
  dateRangeParams: KiSyokenDateRangeParams;
  searchParams: KiSyokenSearchParams;
  baseDate?: Date;
  excludeToday?: boolean;
  customDate?: string;
}):
  | KiSyokenDateRangePeriod
  | {
      ymdList: string[];
      ymdComparisonList: string[];
    } => {
  // カスタムと'平日/土日祝比較'の場合、searchParamsの選択されている日付情報をそのまま戻す
  const dateRange = dateRangeParams.dateRange;
  if (
    dateRange === 'カスタム' ||
    dateRangeParams.comparisonSection === '平日/土日祝比較'
  ) {
    if (
      searchParams.startDate != null &&
      searchParams.endDate != null &&
      searchParams.startComparisonDate != null &&
      searchParams.endComparisonDate != null
    ) {
      return {
        startDate: searchParams.startDate,
        endDate: searchParams.endDate,
        startComparisonDate: searchParams.startComparisonDate,
        endComparisonDate: searchParams.endComparisonDate,
      };
    }
    return {
      ymdList: searchParams.ymdList ?? [],
      ymdComparisonList: searchParams.ymdComparisonList ?? [],
    };
  }

  return makeKiSyokenDateFromDateRangeWithoutCustom({
    dateRangeParams: { ...dateRangeParams, dateRange },
    baseDate,
    excludeToday,
    customDate,
  });
};

/**
 * [機種別商圏] 検索パラメータに渡す日付データを算出する、ただしカスタムは考慮しない
 */
export const makeKiSyokenDateFromDateRangeWithoutCustom = ({
  dateRangeParams,
  baseDate = new Date(),
  excludeToday = true,
  customDate,
}: {
  dateRangeParams: KiSyokenDateRangeParams & {
    dateRange: Exclude<KiSyokenDateRangeParams['dateRange'], 'カスタム'>;
  };
  baseDate?: Date;
  excludeToday?: boolean;
  customDate?: string;
}): KiSyokenDateRangePeriod => {
  const { startDate, endDate } = dateRangeToStartEnd(
    dateRangeParams.dateRange,
    baseDate,
    customDate ?? '1'
  );

  const comparisonSection = dateRangeParams.comparisonSection;
  // 平日/土日祝比較の場合は、期間全体を対象にして分離処理を行うため、
  // この関数では単一の日付範囲を返すことができません
  if (
    comparisonSection === 'カスタム' ||
    comparisonSection === '平日/土日祝比較'
  ) {
    throw new Error(`指定された比較区分 ${comparisonSection} は未実装です`);
  }

  // 今月の前月/前年同月比較の場合：主期間の実際の日数に合わせる
  if (
    dateRangeParams.dateRange === '今月' &&
    (comparisonSection === '前月' || comparisonSection === '前年同月')
  ) {
    const mainStartDate = new Date(startDate);
    const mainEndDate = new Date(endDate);
    const now = new Date();

    // 主期間の日数を算出
    const mainDays = eachDayOfInterval({
      start: mainStartDate,
      end: mainEndDate,
    });
    const actualDays = mainDays.length;

    // excludeToday: 主期間に今日が含まれる場合のみ比較期間の日数を1日減らす（ただし期間が1日の場合は減らさない）
    const todayYmd = format(now, 'yyyy-MM-dd');
    const includesTodayInMain = mainDays
      .map((d) => format(d, 'yyyy-MM-dd'))
      .includes(todayYmd);
    const targetDays =
      excludeToday && includesTodayInMain && actualDays > 1
        ? actualDays - 1
        : actualDays;

    // 比較期間の開始日を計算
    let comparisonStartDate: Date;
    if (comparisonSection === '前月') {
      comparisonStartDate = startOfMonth(add(mainStartDate, { months: -1 }));
    } else {
      // 前年同月
      comparisonStartDate = startOfMonth(add(mainStartDate, { years: -1 }));
    }

    // 比較期間の終了日を計算（開始日から指定日数分）
    const comparisonEndDate = add(comparisonStartDate, {
      days: targetDays - 1,
    });

    return {
      startDate: startDate,
      endDate: endDate,
      startComparisonDate: format(comparisonStartDate, 'yyyy-MM-dd'),
      endComparisonDate: format(comparisonEndDate, 'yyyy-MM-dd'),
    };
  }

  // その他の比較区分：従来の計算方法
  const { startDate: startComparisonDate, endDate: endComparisonDate } =
    dateRangeToStartEnd(
      comparisonSection,
      new Date(startDate),
      customDate ?? '1'
    );

  return {
    startDate: startDate,
    endDate: endDate,
    startComparisonDate,
    endComparisonDate,
  };
};

/**
 * [機種別商圏] 検索パラメータに渡す日付データを期間単位から算出する
 * 期間単位は「日」「週」「月」の3種類のみ対応
 */
export const makeKiSyokenDateFromDateUnit = (
  dateUnit: Exclude<KiSyokenDateUnit, '範囲選択' | '自由選択'>,
  currentDate: Date = new Date()
): {
  ymdList: Date[];
  ymdComparisonList: Date[];
} => {
  // ガード: 無効な日付は本日にフォールバック
  if (Number.isNaN(currentDate.getTime())) {
    currentDate = new Date();
  }
  let startDate: Date, endDate: Date;
  let startComparisonDate: Date, endComparisonDate: Date;
  switch (dateUnit) {
    case '日': {
      startDate = currentDate;
      endDate = currentDate;
      const lastDay = add(currentDate, { days: -1 });
      startComparisonDate = lastDay;
      endComparisonDate = lastDay;
      break;
    }
    case '週': {
      startDate = startOfWeek(currentDate, { weekStartsOn: 1 });
      endDate = endOfWeek(currentDate, { weekStartsOn: 1 });
      const lastWeek = add(currentDate, { days: -7 });
      startComparisonDate = startOfWeek(lastWeek, { weekStartsOn: 1 });
      endComparisonDate = endOfWeek(lastWeek, { weekStartsOn: 1 });

      break;
    }
    case '月': {
      startDate = startOfMonth(currentDate);
      endDate = endOfMonth(currentDate);
      const lastMonth = add(currentDate, { months: -1 });
      startComparisonDate = startOfMonth(lastMonth);
      endComparisonDate = endOfMonth(lastMonth);
      break;
    }
  }
  const today = new Date();
  return {
    // 最終日が本日より大きい場合は本日に補正する
    ymdList: eachDayOfInterval({
      start: startDate,
      end: endDate > today ? today : endDate,
    }),
    ymdComparisonList: eachDayOfInterval({
      start: startComparisonDate,
      end: endComparisonDate,
    }),
  };
};
